import React, { useEffect, useState } from "react";
import {
  Heart, RefreshCw, AlertCircle, Check, Clock, Skull,
  ChevronDown, ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Cron Heartbeat Tile · admin surface.
 *
 * Surfaces every registered cron job's last-successful-fire timestamp
 * against its expected cadence. Buckets jobs into HEALTHY / STALE /
 * DEAD / NEVER. Worst-status jobs sort to the top so you see problems
 * first, not last.
 *
 * The single piece of UX this gives: *"Last successful Zoho sync: 14
 * days ago"* — visible at a glance, would have caught the silent stop
 * on day 2 instead of day 14.
 */
export default function CronHeartbeatTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState({ DEAD: true, STALE: true,
                                              NEVER: true, PENDING: false, HEALTHY: false });

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const token = localStorage.getItem("scaleboard_token");
      const r = await fetch(`${API}/api/admin/cron-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErr(e.message || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);  // auto-refresh every minute
    return () => clearInterval(id);
  }, []);

  const summary = data?.summary || { HEALTHY: 0, STALE: 0, DEAD: 0, NEVER: 0, PENDING: 0 };
  const jobs = data?.jobs || [];
  const groups = {
    DEAD:    jobs.filter((j) => j.status === "DEAD"),
    STALE:   jobs.filter((j) => j.status === "STALE"),
    NEVER:   jobs.filter((j) => j.status === "NEVER"),
    PENDING: jobs.filter((j) => j.status === "PENDING"),
    HEALTHY: jobs.filter((j) => j.status === "HEALTHY"),
  };

  const fmtHrs = (h) => {
    if (h == null) return "—";
    if (h < 1) return `${Math.round(h * 60)} min`;
    if (h < 48) return `${h.toFixed(1)} h`;
    return `${Math.round(h / 24)} d`;
  };

  return (
    <div
      data-testid="cron-heartbeat-tile"
      className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/40 p-6 shadow-sm"
    >
      <div className="mb-5 flex items-start justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <Heart className={`h-5 w-5 ${summary.DEAD > 0 ? "text-rose-600" : "text-emerald-600"}`} />
            <h3 className="font-serif text-xl font-semibold text-slate-900">
              Cron Heartbeat
            </h3>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600">
              {data?.total_jobs || 0} jobs tracked
            </span>
          </div>
          <p className="text-sm italic text-slate-500">
            Last successful fire per cron · auto-refreshes every minute ·
            sorts worst-first.
          </p>
        </div>
        <button
          data-testid="cron-heartbeat-refresh"
          onClick={load}
          disabled={loading}
          className="rounded-full border border-slate-200 bg-white p-2 transition hover:bg-slate-50 disabled:opacity-40"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Summary bar */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <SummaryCard testid="cron-summary-dead" label="Dead" count={summary.DEAD} colour="rose"
                      hint="> 3× expected cadence" Icon={Skull} />
        <SummaryCard testid="cron-summary-stale" label="Stale" count={summary.STALE} colour="amber"
                      hint="1.5–3× expected" Icon={Clock} />
        <SummaryCard testid="cron-summary-never" label="Never fired" count={summary.NEVER} colour="slate"
                      hint="long uptime, zero fires" Icon={AlertCircle} />
        <SummaryCard testid="cron-summary-pending" label="Pending" count={summary.PENDING} colour="sky"
                      hint="first fire not yet due" Icon={Clock} />
        <SummaryCard testid="cron-summary-healthy" label="Healthy" count={summary.HEALTHY} colour="emerald"
                      hint="within expected cadence" Icon={Check} />
      </div>

      {err ? (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {err}
        </div>
      ) : null}

      {loading && !data ? (
        <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
      ) : null}

      {/* Groups */}
      {data ? (
        <div className="space-y-3">
          {["DEAD", "STALE", "NEVER", "PENDING", "HEALTHY"].map((bucket) => {
            const rows = groups[bucket];
            if (!rows.length) return null;
            const colour = {
              DEAD: "rose", STALE: "amber", NEVER: "slate",
              PENDING: "sky", HEALTHY: "emerald",
            }[bucket];
            const ring = {
              DEAD: "ring-rose-200 bg-rose-50/40",
              STALE: "ring-amber-200 bg-amber-50/30",
              NEVER: "ring-slate-200 bg-slate-50/40",
              PENDING: "ring-sky-200 bg-sky-50/30",
              HEALTHY: "ring-emerald-100 bg-emerald-50/20",
            }[bucket];
            const isOpen = expanded[bucket];
            return (
              <div
                key={bucket}
                data-testid={`cron-group-${bucket.toLowerCase()}`}
                className={`overflow-hidden rounded-xl ring-1 ${ring}`}
              >
                <button
                  onClick={() => setExpanded((p) => ({ ...p, [bucket]: !p[bucket] }))}
                  className="flex w-full items-center justify-between px-4 py-2.5 text-left transition hover:bg-white/50"
                >
                  <div className="flex items-center gap-2">
                    {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                    <span className={`text-xs font-bold uppercase tracking-wider text-${colour}-700`}>
                      {bucket} · {rows.length}
                    </span>
                  </div>
                </button>
                {isOpen ? (
                  <div className="overflow-x-auto border-t border-white/40 bg-white/60">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left font-medium">Job</th>
                          <th className="px-3 py-1.5 text-left font-medium">Last fire</th>
                          <th className="px-3 py-1.5 text-left font-medium">Cadence</th>
                          <th className="px-3 py-1.5 text-left font-medium">Last error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((j) => (
                          <tr key={j.job_id}
                              data-testid={`cron-row-${j.job_id}`}
                              className="border-t border-slate-100 transition hover:bg-amber-50/30">
                            <td className="px-3 py-2">
                              <div className="font-medium text-slate-900">{j.label}</div>
                              <div className="font-mono text-[10px] text-slate-400">{j.job_id}</div>
                            </td>
                            <td className="px-3 py-2">
                              <span className={`font-semibold ${
                                j.status === "DEAD" ? "text-rose-700"
                                : j.status === "STALE" ? "text-amber-700"
                                : j.status === "NEVER" ? "text-slate-500 italic"
                                : j.status === "PENDING" ? "text-sky-700 italic"
                                : "text-emerald-700"
                              }`}>
                                {j.hours_ago != null ? `${fmtHrs(j.hours_ago)} ago` : (j.status === "PENDING" ? "awaiting first fire" : "never")}
                              </span>
                              {j.last_ok_at ? (
                                <div className="text-[10px] text-slate-400">
                                  {new Date(j.last_ok_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                                </div>
                              ) : (
                                <div className="text-[10px] italic text-slate-400">never</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-slate-500">
                              every {fmtHrs(j.expected_h)}
                            </td>
                            <td className="px-3 py-2">
                              {j.last_error ? (
                                <div title={j.last_error} className="max-w-xs truncate text-rose-600">
                                  {j.last_error.slice(0, 60)}{j.last_error.length > 60 ? "…" : ""}
                                </div>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <div className="mt-4 text-xs text-slate-400">
        <Clock className="mr-1 inline h-3 w-3" />
        Audited as of {data?.as_of ? new Date(data.as_of).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—"} · auto-refreshes every minute
      </div>
    </div>
  );
}

const SummaryCard = ({ testid, label, count, colour, hint, Icon }) => {
  const c = {
    rose: "from-rose-50 to-rose-100 text-rose-700 ring-rose-200",
    amber: "from-amber-50 to-amber-100 text-amber-700 ring-amber-200",
    slate: "from-slate-50 to-slate-100 text-slate-700 ring-slate-200",
    sky: "from-sky-50 to-sky-100 text-sky-700 ring-sky-200",
    emerald: "from-emerald-50 to-emerald-100 text-emerald-700 ring-emerald-200",
  }[colour];
  return (
    <div data-testid={testid}
         className={`rounded-xl bg-gradient-to-br p-3 ring-1 ${c}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
        <Icon className="h-3 w-3 opacity-50" />
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{count}</div>
      <div className="text-[10px] opacity-70">{hint}</div>
    </div>
  );
};
